/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

export type HomeConfig = {
  homeCosmosApi?: string;
  homeCosmosApiKey?: string;
  homeTripPlannerApi?: string;
  homeRewardsApi?: string;
  homeBigpayApi?: string;
};

type HomeConfigContextType = {
  config: HomeConfig;
};

export const HomeConfigContext = createContext<
  HomeConfigContextType | undefined
>(undefined);

type HomeConfigProviderProps = HomeConfig & {
  children: ReactNode;
};

export const HomeConfigProvider: React.FC<HomeConfigProviderProps> = ({
  children,
  ...initialConfig
}) => {
  const [config, setConfig] = useState<HomeConfig>(initialConfig);
  const updateConfig = useCallback((updates: Partial<HomeConfig>) => {
    setConfig((prev) => ({
      ...prev,
      ...updates,
    }));
  }, []);

  const value = useMemo(
    () => ({
      config,
      updateConfig,
    }),
    [config, updateConfig],
  );

  return (
    <HomeConfigContext.Provider value={value}>
      {children}
    </HomeConfigContext.Provider>
  );
};

export const useHomeConfig = (): HomeConfigContextType => {
  const ctx = useContext(HomeConfigContext);
  if (!ctx)
    throw new Error('useHomeConfig must be used inside HomeConfigProvider');
  return ctx;
};
